'use client'

import React, { createContext, useContext, useState, useEffect } from 'react'

interface Message {
  id: string
  type: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  data?: {
    merchantId?: number
    csvData?: string
    analysis?: any
    settlement?: any
    extractedData?: {
      source: string
      extractionConfidence: number
      transactionCount: number
      rawData: string
    }
  }
}

interface ChatbotContextType {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  isMinimized: boolean
  setIsMinimized: (minimized: boolean) => void
  messages: Message[]
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
  addMessage: (type: 'user' | 'assistant' | 'system', content: string, data?: any) => void
  merchantId: string
  setMerchantId: (id: string) => void
  csvData: string
  setCsvData: (data: string) => void
  analysisResult: any
  setAnalysisResult: (result: any) => void
  uploadedFile: File | null
  setUploadedFile: (file: File | null) => void
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  clearConversation: () => void
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined)

// Storage keys
const STORAGE_KEYS = {
  messages: 'chatbot_messages',
  merchantId: 'chatbot_merchantId',
  csvData: 'chatbot_csvData',
  analysisResult: 'chatbot_analysisResult',
  isMinimized: 'chatbot_minimized'
}

// Utility functions for localStorage
const saveToStorage = (key: string, value: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.warn('Failed to save to localStorage:', error)
  }
}

const loadFromStorage = (key: string, defaultValue: any = null) => {
  try {
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : defaultValue
  } catch (error) {
    console.warn('Failed to load from localStorage:', error)
    return defaultValue
  }
}

export function ChatbotProvider({ children }: { children: React.ReactNode }) {
  // Initialize state with localStorage data
  const [isOpen, setIsOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(true)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'system',
      content: '🤖 **AI Assistant**\n\nI can help you with:\n• 💰 **Settlement Information** - "How much to send to email@example.com for latest settlement?"\n• 📊 **Transaction Analysis** - Upload files or ask about merchant transactions\n• 🔍 **Smart Matching** - Analyze CSV/PDF/images against platform data\n• ⚠️ **Discrepancy Detection** - Find mismatches and issues\n• 💸 **Payment Guidance** - Get exact amounts and dates\n\n**Just ask naturally:** "How much money do I need to send to perfume.team.experts@gmail.com?"',
      timestamp: new Date()
    }
  ])
  const [merchantId, setMerchantId] = useState('')
  const [csvData, setCsvData] = useState('')
  const [analysisResult, setAnalysisResult] = useState<any>(null)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Load saved state on mount
  useEffect(() => {
    const savedMessages = loadFromStorage(STORAGE_KEYS.messages)
    const savedMerchantId = loadFromStorage(STORAGE_KEYS.merchantId, '')
    const savedCsvData = loadFromStorage(STORAGE_KEYS.csvData, '')
    const savedAnalysisResult = loadFromStorage(STORAGE_KEYS.analysisResult)
    const savedIsMinimized = loadFromStorage(STORAGE_KEYS.isMinimized, true)

    if (savedMessages && savedMessages.length > 1) { // Keep system message if no saved messages
      // Convert timestamp strings back to Date objects
      const messagesWithDates = savedMessages.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }))
      setMessages(messagesWithDates)
    }
    
    setMerchantId(savedMerchantId)
    setCsvData(savedCsvData)
    setAnalysisResult(savedAnalysisResult)
    setIsMinimized(savedIsMinimized)
  }, [])

  // Save state changes to localStorage
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.messages, messages)
  }, [messages])

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.merchantId, merchantId)
  }, [merchantId])

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.csvData, csvData)
  }, [csvData])

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.analysisResult, analysisResult)
  }, [analysisResult])

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.isMinimized, isMinimized)
  }, [isMinimized])

  const addMessage = (type: 'user' | 'assistant' | 'system', content: string, data?: any) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      type,
      content,
      timestamp: new Date(),
      data
    }
    setMessages(prev => [...prev, newMessage])
  }

  const clearConversation = () => {
    const systemMessage = {
      id: '1',
      type: 'system' as const,
      content: '🤖 **AI Assistant**\n\nI can help you with:\n• 💰 **Settlement Information** - "How much to send to email@example.com for latest settlement?"\n• 📊 **Transaction Analysis** - Upload files or ask about merchant transactions\n• 🔍 **Smart Matching** - Analyze CSV/PDF/images against platform data\n• ⚠️ **Discrepancy Detection** - Find mismatches and issues\n• 💸 **Payment Guidance** - Get exact amounts and dates\n\n**Just ask naturally:** "How much money do I need to send to perfume.team.experts@gmail.com?"',
      timestamp: new Date()
    }
    setMessages([systemMessage])
    setCsvData('')
    setAnalysisResult(null)
    setUploadedFile(null)
    // Keep merchantId as it might be useful across conversations
  }

  const value = {
    isOpen,
    setIsOpen,
    isMinimized,
    setIsMinimized,
    messages,
    setMessages,
    addMessage,
    merchantId,
    setMerchantId,
    csvData,
    setCsvData,
    analysisResult,
    setAnalysisResult,
    uploadedFile,
    setUploadedFile,
    isLoading,
    setIsLoading,
    clearConversation
  }

  return (
    <ChatbotContext.Provider value={value}>
      {children}
    </ChatbotContext.Provider>
  )
}

export function useChatbot() {
  const context = useContext(ChatbotContext)
  if (context === undefined) {
    throw new Error('useChatbot must be used within a ChatbotProvider')
  }
  return context
}
